import { KeyValueStore } from './KeyValueStore.ts';
import { rawLegacyStateNames, TimeElapsedKey } from './State.tsx';
import { track } from './umami.ts';
import { Capacitor } from '@capacitor/core';

export const API_BASE_URL = runningAsNativeApp() ? window.API_BASE_URL : '';

export function applyTimeout<T>(promise: Promise<T>, delay: number = 5000): Promise<T> {
    return Promise.race([
        new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`Request timed out after ${delay}ms`)), delay)),
        promise,
    ]);
}

export function isPuzzleKey(key: string) {
    if (key.match(/^triplit_[a-zA-Z0-9]+$/) !== null) {
        return true;
    }
    const match = key.match(/^triplit_[a-zA-Z0-9]+_([a-zA-Z0-9-]+)$/);
    if (match == null) {
        return false;
    }
    if (match[1] === 'timeElapsed') {
        return false;
    }
    return true;
}

export function isTimeElapsedKey(key: string) {
    return key.match(/^triplit_[a-zA-Z0-9]+_([a-zA-Z0-9-]+_)?timeElapsed$/) !== null;
}

export function isLegacyGlobalStateKey(key: string) {
    const name = key.split('_')[0];
    return (rawLegacyStateNames as readonly string[]).includes(name);
}

export function isGameWideStateKey(key: string) {
    return key.match(/^gameWideState(_[a-zA-Z0-9-]+)?$/) !== null;
}

export function toGameWideStateKey(userId: string) {
    return `gameWideState_${userId}`;
}

export async function readTimeElapsedState(tripleId: string, store: KeyValueStore): Promise<number | null> {
    const timeElapsed = await store.getValue(new TimeElapsedKey(tripleId).toString());
    if (!timeElapsed) {
        return null;
    }
    return JSON.parse(timeElapsed) as number;
}

function mod(n: number, m: number) {
    return n < 0 ? (n % m) + m : n % m;
}

function formatTimeElapsed(timeElapsed: number) {
    const minutes = Math.floor(timeElapsed / 60);
    const seconds = timeElapsed % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function getStyleValue(property: string) {
    return window.getComputedStyle(document.documentElement).getPropertyValue(property).trim();
}

export enum TrafficSource {
    FACEBOOK,
    GOOGLE,
    REDDIT,
    TIKTOK,
    LINKEDIN,
    X,
    OTHER,
    NONE,
}

export function getTrafficSource(): TrafficSource {
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');
    const googleClickId = urlParams.get('gclid');
    const redditClickId = urlParams.get('rdt_cid');
    const tikTokClickId = urlParams.get('ttclid');
    const linkedInClickId = urlParams.get('li_fat_id');
    const isX = urlParams.get('twlcid') || urlParams.get('utm_source') === 'x';
    if (fbclid) {
        return TrafficSource.FACEBOOK;
    } else if (googleClickId) {
        return TrafficSource.GOOGLE;
    } else if (redditClickId) {
        return TrafficSource.REDDIT;
    } else if (tikTokClickId) {
        return TrafficSource.TIKTOK;
    } else if (linkedInClickId) {
        return TrafficSource.LINKEDIN;
    } else if (isX) {
        return TrafficSource.X;
    } else if (urlParams.get('utm_source')) {
        return TrafficSource.OTHER;
    }
    return TrafficSource.NONE;
}

function logConversion(): void {
    const trafficSource = getTrafficSource();
    switch (trafficSource) {
        case TrafficSource.FACEBOOK:
            track('facebook_conversion');
            return;
        case TrafficSource.GOOGLE:
            track('google_conversion');
            return;
        case TrafficSource.REDDIT:
            track('reddit_conversion');
            return;
        case TrafficSource.TIKTOK:
            track('tiktok_conversion');
            return;
        case TrafficSource.LINKEDIN:
            track('linkedin_conversion');
            return;
        case TrafficSource.X:
            track('x_conversion');
            return;
        case TrafficSource.OTHER:
            track('other_conversion');
            return;
        case TrafficSource.NONE:
        default:
            track('organic_conversion');
            return;
    }
}

export function flashNotification(setter: (arg: boolean) => void, duration: number) {
    setter(true);
    setTimeout(() => {
        setter(false);
    }, duration);
}

export function runningAsNativeApp() {
    return Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
}

export function dateToTimeString(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
}

export const MODAL_CONTENT_DEFAULTS = {
    inset: 0,
    position: 'relative',
    backgroundColor: 'var(--body-background-color)',
    color: 'var(--default-text-color)',
    border: '1px solid var(--default-text-color)',
};

export const MODAL_OVERLAY_DEFAULTS = {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
};

export class UserCancellationError extends Error {
    constructor(message?: string) {
        super(message);
        this.name = 'UserCancellationError';
    }
}

export class InsufficientEntitlementError extends Error {
    constructor(message?: string) {
        super(message);
        this.name = 'InsufficientEntitlementError';
    }
}

// TODO: consider using a library for deep equality checks.
// This function doesn't handle circular references, which we absolutely don't expect
// for our data structures, but is something to be aware of.
export function deepEquals(a: any, b: any): boolean {
    if (a === b) {
        return true;
    }
    if (typeof a !== 'object' || typeof b !== 'object' || a == null || b == null) {
        return false;
    }

    // Handle arrays separately
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (!deepEquals(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }

    // Handle objects
    if (Array.isArray(a) !== Array.isArray(b)) {
        return false;
    }

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) {
        return false;
    }
    for (const key of aKeys) {
        if (!bKeys.includes(key)) {
            return false;
        }
        if (!deepEquals(a[key], b[key])) {
            return false;
        }
    }
    return true;
}

export const PREMIUM_ENTITLEMENT_NAME: string = 'Pro';

export function getiOSAppStoreWebLink(reviewPrompt = false) {
    return `https://apps.apple.com/us/app/triplit-daily-word-game/id6736955154${
        reviewPrompt ? '?action=write-review' : ''
    }`;
}

export function getAndroidPlayStoreWebLink() {
    return 'https://play.google.com/store/apps/details?id=com.gg.triplit';
}

export function getAppStoreWebLink(reviewPrompt = false) {
    // We have already verified that either isOS() or isAndroid() is true before calling this.
    if (isIOS()) {
        return getiOSAppStoreWebLink(reviewPrompt);
    } else if (isAndroid()) {
        return getAndroidPlayStoreWebLink();
    }
}

export function isCandidateForApp() {
    return Capacitor.getPlatform() === 'web' && (isIOS() || isAndroid());
}

export function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

export function isIOS() {
    return /iPhone|iPad/i.test(navigator.userAgent) || Capacitor.getPlatform() === 'ios';
}

export function isAndroid() {
    return /Android/i.test(navigator.userAgent) || Capacitor.getPlatform() === 'android';
}

export async function canReachTriplit(): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    try {
        await fetch(`${API_BASE_URL}/health`, {
            method: 'HEAD',
            signal: controller.signal,
        });
        return true;
    } catch {
        return false;
    } finally {
        clearTimeout(timeoutId);
    }
}

export { mod, formatTimeElapsed, logConversion, getStyleValue };
